/**
 * Color Configuration - Facade for primitive tokens
 *
 * This file provides backward-compatible color access using the
 * 'color-scale' naming convention (e.g., 'gray-500', 'blue-600').
 *
 * Single source of truth: primitive.tokens.ts
 *
 * For new code, prefer:
 * - Tailwind: bg-gray-500, text-blue-600 (from theme.extend.colors)
 * - PrimeNG: {primary.color}, {surface.500} token references
 */
import {
  grayColors,
  blueColors,
  greenColors,
  redColors,
  yellowColors,
  orangeColors,
  aiColors
} from '../../tokens/primitive.tokens';

export type ColorMap = Record<string, string>;

/**
 * Transforms a color palette object into flat 'name-scale' entries
 * Example: { 500: '#AEB7C3' } -> { 'gray-500': '#AEB7C3' }
 */
function flattenPalette(
  name: string,
  palette: Record<number | string, string>
): ColorMap {
  return Object.entries(palette).reduce((acc, [scale, value]) => {
    // Handle special case: 0 -> '000' for backward compatibility
    const key = scale === '0' ? `${name}-000` : `${name}-${scale}`;
    acc[key] = value;
    return acc;
  }, {} as ColorMap);
}

/**
 * All design system colors derived from primitive tokens.
 * Use this for programmatic color access (charts, dynamic styling).
 * For Tailwind, use utility classes like `bg-gray-500` or `bg-primary`.
 */
export const colors: ColorMap = {
  ...flattenPalette('gray', grayColors),
  ...flattenPalette('blue', blueColors),
  ...flattenPalette('green', greenColors),
  ...flattenPalette('red', redColors),
  ...flattenPalette('yellow', yellowColors),
  ...flattenPalette('orange', orangeColors),
  ...flattenPalette('ai', aiColors),
  'purple-500': '#D59AE4',
  'purple-700': '#696cff',
  'red-50': '#fdf5f5',
  'red-800': '#f0d0d0'
};
